import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
} from "react";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
	ContextMenu,
	type ContextMenuEntry,
	type ContextMenuPosition,
} from "@/components/common/ContextMenu";
import { FileTreeSkeleton } from "@/components/file-tree/FileTreeSkeleton";
import { NewFileDialog } from "@/components/file-tree/NewFileDialog";
import { NewFolderDialog } from "@/components/file-tree/NewFolderDialog";
import { RenameDialog } from "@/components/file-tree/RenameDialog";
import { useEnvironmentUpload } from "@/components/file-tree/useEnvironmentUpload";
import {
	TREE_HOVER_GROUP_CLASS,
	TREE_INDENT_PX,
	TREE_ROOT_PADDING_PX,
} from "@/components/file-tree/TreeIndentGuides";
import { sharedVolumeEntryMenuItems } from "@/components/volumes/shared-volume-file-actions";
import { SharedVolumeLoadMoreRow } from "@/components/volumes/SharedVolumeLoadMoreRow";
import { SharedVolumeTreeRowFrame } from "@/components/volumes/SharedVolumeTreeRowFrame";
import { useCloudSharedVolumeDrop } from "@/components/volumes/useSharedVolumeDrop";
import {
	useSharedVolumeTreeExpansion,
	useSharedVolumeTreeSelection,
} from "@/components/volumes/useSharedVolumeTreeNavigation";
import {
	createCloudSharedVolumeEntry,
	deleteCloudSharedVolumeEntries,
	moveCloudSharedVolumeEntries,
	renameCloudSharedVolumeEntry,
} from "@/lib/cloud-shared-volume-entries";
import {
	buildCloudSharedVolumeRows,
	canMoveCloudSharedVolumeEntries,
	cloudSharedVolumeContainerPath,
	cloudSharedVolumeDeleteRequest,
	cloudSharedVolumeDropDirectory,
	cloudSharedVolumeEditActions,
	cloudSharedVolumeRowKey,
	isCloudSharedVolumeEntryUnsupported,
	type CloudSharedVolumeEntryRow,
} from "@/lib/cloud-shared-volumes";
import type { CloudSharedVolumeEntryPayload } from "@/lib/dnd/payload";
import { useHasPermission } from "@/lib/permissions";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";
import {
	selectCanInsertPathIntoMessage,
	selectEnvironmentUploadOperation,
	selectIsChatEnvironmentStopped,
	useChatStore,
} from "@/stores/useChatStore";
import { useCloudSharedVolumeStore } from "@/stores/useCloudSharedVolumeStore";

const NOT_MOUNTED_REASON = "次回起動から使えます。";
const UNSUPPORTED_REASON = "シンボリックリンクや特殊なファイルは開けません。";

/** 行の左のシェブロン・アイコンの枠と、名前との間の幅。 */
const ROW_LEAD_PX = 22;

/** 根の直下の行の名前と頭を揃えた注記。 */
const NOTE_STYLE: CSSProperties = {
	margin: 0,
	padding: `2px 12px 2px ${TREE_ROOT_PADDING_PX + TREE_INDENT_PX + ROW_LEAD_PX}px`,
	fontSize: "var(--text-caption)",
	color: "var(--fg-3)",
};

interface MenuState {
	position: ContextMenuPosition;
	row: CloudSharedVolumeEntryRow;
	/** 右クリックした行を含む、選んでいる行。 */
	rows: CloudSharedVolumeEntryRow[];
}

interface CreateTarget {
	kind: "file" | "folder";
	volumeId: string;
	directory: string;
}

interface DeleteTarget {
	volumeId: string;
	paths: string[];
	message: string;
}

const selectCloudTree = () => useCloudSharedVolumeStore.getState().selectTree("cloud");

function rowReason(row: CloudSharedVolumeEntryRow): string | undefined {
	if (row.entry === null) return row.volume.mounted ? undefined : NOT_MOUNTED_REASON;
	return isCloudSharedVolumeEntryUnsupported(row.entry) ? UNSUPPORTED_REASON : undefined;
}

/**
 * クラウドの環境に付いた共有ボリュームを最上位のフォルダにして出す。中身は環境に mount されたボリュームをその場で読む。
 * 起動の後に付けたもの (mount されていないもの) は灰色にして開けない。右クリックでファイルを開く・ダウンロードし、パスを使う。
 * エージェント管理の権限があれば、PC のファイル・フォルダを行に落とすか右クリックのアップロードで選んで、共有ボリュームに置く。
 * ~ のツリーから行にドラッグしたものはコピーする。新しいファイル・フォルダの作成・名前の変更・削除と、同じボリュームの中の
 * ドラッグでの移動もできる。どれも付けている他の環境にすぐ効き、戻せない。
 */
export function CloudSharedVolumesTree() {
	const canInsertPath = useChatStore(selectCanInsertPathIntoMessage);
	const stopped = useChatStore(selectIsChatEnvironmentStopped);
	const volumes = useCloudSharedVolumeStore((s) => s.volumes);
	const directories = useCloudSharedVolumeStore((s) => s.directories);
	const loadingDirectories = useCloudSharedVolumeStore((s) => s.loadingDirectories);
	const environmentId = useCloudSharedVolumeStore((s) => s.environmentId);
	const canEdit = useHasPermission("manageAgents") && !stopped;
	const [menu, setMenu] = useState<MenuState | null>(null);
	const [creating, setCreating] = useState<CreateTarget | null>(null);
	const [renaming, setRenaming] = useState<CloudSharedVolumeEntryRow | null>(null);
	const [deleting, setDeleting] = useState<DeleteTarget | null>(null);
	// 移動もアップロード・~ からのコピーと同じ環境の排他を取り、その間はまだ元の場所に出ている行を続けて受けない。
	const uploading = useChatStore(
		(s) => selectEnvironmentUploadOperation(s, environmentId) !== null,
	);
	// 別の環境に移ったら、前の環境に向けた操作を閉じる。
	useEffect(() => {
		setCreating(null);
		setRenaming(null);
		setDeleting(null);
	}, [environmentId]);
	const {
		dropDirectory,
		dropOsFiles,
		dropHomeEntries,
		uploadDirectory,
		uploadPickedFiles,
		overwriteDialog,
	} = useCloudSharedVolumeDrop();
	/** 右クリックのアップロードで選んだ行。ファイルを選び終えたときに使う。 */
	const uploadRowRef = useRef<CloudSharedVolumeEntryRow | null>(null);
	const pickedFilesUpload = useCallback(
		async (files: File[]) => {
			const row = uploadRowRef.current;
			if (row === null) return;
			// 失敗は知らせ済み。
			await uploadPickedFiles(row, files).catch((err: unknown) => {
				console.error("共有ボリュームにアップロードできませんでした。", err);
			});
		},
		[uploadPickedFiles],
	);
	const { uploadInputs, uploadMenuItems } = useEnvironmentUpload("", "cloud-shared-volume-upload", {
		upload: pickedFilesUpload,
	});

	const loadChildren = useCallback((row: CloudSharedVolumeEntryRow) => {
		const store = useCloudSharedVolumeStore.getState();
		return store.directories.has(row.key) ? null : store.loadDirectory(row.volumeId, row.path);
	}, []);
	const { expanded, toggleExpanded } = useSharedVolumeTreeExpansion(loadChildren);

	const rows = useMemo(
		() => buildCloudSharedVolumeRows(volumes ?? [], directories, expanded),
		[directories, expanded, volumes],
	);
	const entryRows = useMemo(
		() => rows.filter((row): row is CloudSharedVolumeEntryRow => row.type === "entry"),
		[rows],
	);
	const renamable = useCallback(
		(row: CloudSharedVolumeEntryRow) => cloudSharedVolumeEditActions(row, canEdit).renameAndDelete,
		[canEdit],
	);
	const requestDelete = useCallback(
		(targets: CloudSharedVolumeEntryRow[]) => {
			const request = canEdit ? cloudSharedVolumeDeleteRequest(targets) : null;
			if (request !== null) setDeleting(request);
		},
		[canEdit],
	);
	const {
		selected,
		treeRef,
		currentKey,
		selectRow,
		selectForMenu,
		handleRowKeyDown,
		clearSelection,
		clearSelectionOnBlank,
	} = useSharedVolumeTreeSelection({
		entryRows,
		toggleExpanded,
		menuOpen: menu !== null,
		renamable,
		onRename: setRenaming,
		onDelete: requestDelete,
		onSelect: selectCloudTree,
	});

	// ~ の行と共有ボリュームの行は、後に選んだ方だけを選んだままにする (新規作成・アップロードの先がどちらか分かるように)。
	const selectedTree = useCloudSharedVolumeStore((s) => s.selectedTree);
	useEffect(() => {
		if (selectedTree.tree === "home") clearSelection();
	}, [clearSelection, selectedTree]);
	// ペインの空白のクリックなど、外で選択を外したとき。
	const storedSelection = useCloudSharedVolumeStore((s) => s.selection);
	useEffect(() => {
		if (storedSelection === null) clearSelection();
	}, [clearSelection, storedSelection]);

	const selectedRow = entryRows.find((row) => selected.has(row.key));
	const selectionVolumeId = selectedRow?.volumeId ?? null;
	const selectionDirectory =
		selectedRow === undefined || !cloudSharedVolumeEditActions(selectedRow, canEdit).create
			? null
			: cloudSharedVolumeDropDirectory(selectedRow);
	useEffect(() => {
		useCloudSharedVolumeStore
			.getState()
			.setSelection(
				selectionVolumeId === null
					? null
					: { volumeId: selectionVolumeId, directory: selectionDirectory },
			);
	}, [selectionDirectory, selectionVolumeId]);
	useEffect(() => () => useCloudSharedVolumeStore.getState().setSelection(null), []);

	const openMenu = useCallback(
		(row: CloudSharedVolumeEntryRow, event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			const keys = selectForMenu(row);
			const rows = entryRows.filter((candidate) => keys.has(candidate.key));
			// 複数を選んでいれば削除だけ。mount されていないボリュームには使える項目が無い。
			if (
				rows.length > 1
					? cloudSharedVolumeDeleteRequest(rows) === null || !canEdit
					: !row.volume.mounted
			)
				return;
			setMenu({ position: { x: event.clientX, y: event.clientY }, row, rows });
		},
		[canEdit, entryRows, selectForMenu],
	);

	const dragPayload = useCallback(
		(row: CloudSharedVolumeEntryRow): CloudSharedVolumeEntryPayload | null => {
			if (!renamable(row)) return null;
			const rows = selected.has(row.key)
				? entryRows.filter((candidate) => selected.has(candidate.key) && renamable(candidate))
				: [row];
			return {
				kind: "cloud-shared-volume-entry",
				volumeId: row.volumeId,
				entries: rows
					.filter((candidate) => candidate.volumeId === row.volumeId)
					.map((candidate) => ({
						path: candidate.path,
						entryType: candidate.directory ? "directory" : "file",
						name: candidate.name,
					})),
			};
		},
		[entryRows, renamable, selected],
	);

	const moveEntries = useCallback(
		(row: CloudSharedVolumeEntryRow, payload: CloudSharedVolumeEntryPayload) => {
			const directory = cloudSharedVolumeDropDirectory(row);
			if (environmentId === null || directory === null) return;
			const operationId = useChatStore.getState().beginEnvironmentUpload(environmentId);
			if (operationId === null) return;
			clearSelection();
			void moveCloudSharedVolumeEntries(
				environmentId,
				payload.volumeId,
				payload.entries.map((entry) => entry.path),
				directory,
			)
				.catch((err: unknown) => {
					// 失敗は知らせ済み。
					console.error("共有ボリュームの中で移動できませんでした。", err);
				})
				.finally(() => {
					useChatStore.getState().endEnvironmentUpload(environmentId, operationId);
				});
		},
		[clearSelection, environmentId],
	);

	const loadMore = useCallback((volumeId: string, path: string) => {
		reportSharedVolumeFailure(
			useCloudSharedVolumeStore.getState().loadMore(volumeId, path),
			"共有ボリュームの中身を取得できませんでした。",
		);
	}, []);

	const blockDrop = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		event.preventDefault();
		event.dataTransfer.dropEffect = "none";
	}, []);

	const menuItems = useMemo((): ContextMenuEntry[] => {
		if (menu === null) return [];
		const { row, rows } = menu;
		const deleteItem: ContextMenuEntry = {
			label: "削除",
			testId: "shared-volume-menu-delete",
			onSelect: () => requestDelete(rows),
		};
		if (rows.length > 1) return [deleteItem];
		const items = sharedVolumeEntryMenuItems({
			currentEnvironmentId: () => useCloudSharedVolumeStore.getState().environmentId,
			volumeId: row.volumeId,
			path: row.path,
			openable: row.entry?.kind === "file",
			running: !stopped,
			pathToCopy: cloudSharedVolumeContainerPath(row.volume, row.path),
			canInsertPath,
		});
		if (uploadDirectory(row) !== null) {
			if (items.length > 0) items.push({ separator: true });
			items.push(
				...uploadMenuItems.map((item) => ({
					...item,
					onSelect: () => {
						uploadRowRef.current = row;
						item.onSelect?.();
					},
				})),
			);
		}
		const actions = cloudSharedVolumeEditActions(row, canEdit);
		const directory = cloudSharedVolumeDropDirectory(row);
		const edits: ContextMenuEntry[] = [];
		if (actions.create && directory !== null) {
			const create = (kind: CreateTarget["kind"]) => () =>
				setCreating({ kind, volumeId: row.volumeId, directory });
			edits.push(
				{ label: "新規ファイル", testId: "shared-volume-menu-new-file", onSelect: create("file") },
				{
					label: "新規フォルダ",
					testId: "shared-volume-menu-new-folder",
					onSelect: create("folder"),
				},
			);
		}
		if (actions.renameAndDelete) {
			edits.push(
				{
					label: "名前を変更",
					testId: "shared-volume-menu-rename",
					onSelect: () => setRenaming(row),
				},
				deleteItem,
			);
		}
		if (edits.length > 0 && items.length > 0) items.push({ separator: true });
		return [...items, ...edits];
	}, [canEdit, canInsertPath, menu, requestDelete, stopped, uploadDirectory, uploadMenuItems]);

	const createEntry = useCallback(
		async (name: string) => {
			if (creating === null || environmentId === null) return;
			await createCloudSharedVolumeEntry(
				environmentId,
				creating.volumeId,
				creating.directory,
				name,
				creating.kind,
			);
			setCreating(null);
		},
		[creating, environmentId],
	);

	return (
		<div
			onClick={clearSelectionOnBlank}
			onContextMenu={(event) => {
				event.preventDefault();
				event.stopPropagation();
			}}
			onDragEnter={blockDrop}
			onDragOver={blockDrop}
			onDrop={blockDrop}
			className={TREE_HOVER_GROUP_CLASS}
			data-testid="cloud-shared-volumes-pane"
		>
			<div ref={treeRef} data-testid="cloud-shared-volumes-tree">
				{rows.map((row) => {
					if (row.type === "loadMore") {
						const directoryKey = cloudSharedVolumeRowKey(row.volumeId, row.path);
						return (
							<SharedVolumeLoadMoreRow
								key={row.key}
								directoryKey={directoryKey}
								depth={row.depth}
								loading={loadingDirectories.has(directoryKey)}
								onClick={() => loadMore(row.volumeId, row.path)}
							/>
						);
					}
					const directory = directories.get(row.key);
					const rootLoading = row.entry === null && row.expanded && directory === undefined;
					const rootEmpty = row.entry === null && row.expanded && directory?.entries.length === 0;
					const reason = rowReason(row);
					return (
						<Fragment key={row.key}>
							<SharedVolumeTreeRowFrame
								rowKey={row.key}
								name={row.name}
								depth={row.depth}
								directory={row.directory}
								expandable={row.expandable}
								expanded={row.expanded}
								mountPath={row.entry === null ? row.volume.mountPath : null}
								muted={reason !== undefined}
								selected={selected.has(row.key)}
								current={row.key === currentKey}
								spinner={loadingDirectories.has(row.key)}
								title={reason}
								drop={{
									canDrop: dropDirectory(row) !== null,
									onDropFiles: (dataTransfer) => dropOsFiles(row, dataTransfer),
									onDropHomeEntries: (payload) => dropHomeEntries(row, payload),
									canMoveEntries: (payload) =>
										canEdit && !uploading && canMoveCloudSharedVolumeEntries(payload, row),
									onMoveEntries: (payload) => moveEntries(row, payload),
								}}
								dragPayload={dragPayload(row)}
								onClick={(event) => selectRow(row, event)}
								onContextMenu={(event) => openMenu(row, event)}
								onKeyDown={(event) => handleRowKeyDown(row, event)}
							/>
							{rootLoading && <FileTreeSkeleton />}
							{rootEmpty && (
								<p style={NOTE_STYLE} data-testid={`shared-volume-empty-${row.volumeId}`}>
									ファイルがありません
								</p>
							)}
						</Fragment>
					);
				})}
			</div>
			{menu && (
				<ContextMenu
					position={menu.position}
					onClose={() => setMenu(null)}
					testId="shared-volume-menu"
					items={menuItems}
				/>
			)}
			{uploadInputs}
			{overwriteDialog}
			<NewFileDialog
				open={creating?.kind === "file"}
				onConfirm={createEntry}
				onCancel={() => setCreating(null)}
			/>
			<NewFolderDialog
				open={creating?.kind === "folder"}
				onConfirm={createEntry}
				onCancel={() => setCreating(null)}
			/>
			<RenameDialog
				open={renaming !== null}
				initialName={renaming?.name ?? ""}
				onConfirm={async (newName) => {
					if (renaming === null || environmentId === null) return;
					await renameCloudSharedVolumeEntry(
						environmentId,
						renaming.volumeId,
						renaming.path,
						newName,
					);
					setRenaming(null);
					clearSelection();
				}}
				onCancel={() => setRenaming(null)}
			/>
			<ConfirmDialog
				open={deleting !== null}
				title="削除の確認"
				message={deleting?.message ?? ""}
				confirmLabel="削除"
				onConfirm={async () => {
					if (deleting === null || environmentId === null) return;
					await deleteCloudSharedVolumeEntries(environmentId, deleting.volumeId, deleting.paths);
					setDeleting(null);
					clearSelection();
				}}
				onCancel={() => setDeleting(null)}
			/>
		</div>
	);
}
